import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import axios from "axios";
import { companyId, userId } from "../../../utils/companyId";
import { validateNumber } from "../../../utils/validateFields";
import { numberError } from "../../../utils/validationMessage";
import SelectField from "../../../Components/Masters/SelectField";
import FileField from "../../../Components/Masters/FileField";
import InputField from "../../../Components/Masters/InputField";
import EditDialog from "../../../Components/Common/EditDialog";
import MonthYearPicker from "../../../Components/Common/MonthYearPicker";
import { post, put, get, api } from "../../../utils/api";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";

/** Normalize saved unit for form state (strict labels used by the UI). */
const normalizeWasteQtyUnit = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const u = String(raw).trim().toLowerCase();
  if (u === "tonnes" || u === "tons") return "Tonnes";
  if (u === "cubic meter" || u === "cubic metre") return "Cubic Meter";
  return null;
};

/** Matches "Waste Water", "Wastewater", etc. from Waste Category options */
const isWastewaterWasteCategory = (wasteCategory) =>
  String(wasteCategory ?? "")
    .toLowerCase()
    .replace(/\s+/g, "") === "wastewater";

/** Effluent / Sewage: density + volumetric generated quantity (same UX as wastewater category). */
const isDensityRequiredWasteType = (wasteType) => {
  const t = String(wasteType ?? "").trim();
  return t === "Effluent" || t === "Sewage";
};

/** Density field + Cubic Metre/Tonnes row for generated quantity. */
const requiresDensityAndVolumetricRow = (fd) => {
  if (!fd) return false;
  return (
    isWastewaterWasteCategory(fd.wasteCategory) ||
    isDensityRequiredWasteType(fd.wasteType)
  );
};

/** Larger unit labels — Waste Generated drawer only (inputSx; shared InputField unchanged). */
const WASTE_QTY_UNIT_ADORNMENT_SX = {
  "& .MuiInputAdornment-root > .MuiBox-root": {
    fontSize: "15px",
  },
};

/** Layout + larger select value font for generated-quantity unit (wrapper only). */
const WASTE_GEN_UNIT_SELECT_COLUMN_SX = {
  flex: "0 0 38%",
  minWidth: "140px",
  maxWidth: { sm: "220px" },
  "& .MuiSelect-select": { fontSize: "15px", lineHeight: 1.25 },
};

/**
 * Match SelectField / other drawer fields (~14px horizontal on the root).
 */
const WASTE_QTY_INPUT_ROOT_MATCH_SELECT = {
  "& .MuiInputBase-root": {
    minHeight: "40px",
    height: "40px",
    boxSizing: "border-box",
    padding: "8px 14px",
  },
  "& .MuiInputBase-input": {
    textAlign: "left",
    boxSizing: "border-box",
  },
};

/** Wastewater quantity/density row: tighter left inset (3px) so text does not sit too far right of the border. */
const WASTE_QTY_INPUT_ROOT_WASTEWATER = {
  "& .MuiInputBase-root": {
    minHeight: "40px",
    height: "40px",
    boxSizing: "border-box",
    padding: "8px 14px 8px 3px",
  },
  "& .MuiInputBase-input": {
    textAlign: "left",
    boxSizing: "border-box",
  },
};

const WASTE_QTY_VALUE_WITH_UNIT_SX = {
  ...WASTE_QTY_INPUT_ROOT_MATCH_SELECT,
  ...WASTE_QTY_UNIT_ADORNMENT_SX,
};

const WASTE_QTY_VALUE_WITH_UNIT_SX_WASTEWATER = {
  ...WASTE_QTY_INPUT_ROOT_WASTEWATER,
  ...WASTE_QTY_UNIT_ADORNMENT_SX,
};

function AddData({
  inputData,
  fetchData,
  id,
  title,
  element,
  setAddData,
  addData,
  setsnackOpen,
  addUrl,
  updateUrl,
  handleClose,
  /** Map of waste category label -> allowed waste type strings (from WasteGenerated.jsx). */
  wasteTypesByCategory = null,
  /** Map of waste type -> allowed management methods (from WasteGenerated.jsx). */
  wasteManagementMethodsByWasteType = null,
}) {
  const { user } = useUser();

  const [errors, setErrors] = useState({});

  const initialState = {
    consumptionPeriod: {
      month: element?.consumptionPeriod?.month || "",
      year: element?.consumptionPeriod?.year || "",
    },
    wasteCategory: element?.wasteCategory || "",
    wasteType: element?.wasteType || "",
    quantityOfWasteGenerated: {
      value: element?.quantityOfWasteGenerated?.value || "",
      unit:
        normalizeWasteQtyUnit(element?.quantityOfWasteGenerated?.unit) ??
        (requiresDensityAndVolumetricRow(element) ? "" : "Tonnes"),
    },
    wasteManagementMethod: element?.wasteManagementMethod || "",
    vendorName: element?.vendorName?._id || "",
    density: element?.density || "",
    quantityOfWasteManaged: {
      value: element?.quantityOfWasteManaged?.value || "",
      unit:
        normalizeWasteQtyUnit(element?.quantityOfWasteManaged?.unit) ??
        normalizeWasteQtyUnit(element?.quantityOfWasteGenerated?.unit) ??
        (requiresDensityAndVolumetricRow(element) ? "" : "Tonnes"),
    },
    documents: element?.documents || [],
    editRemarkReason: element?.editRemarkReason || "",
  };

  const [formData, setFormData] = useState(initialState);
  const [formData2, setFormData2] = useState(initialState);
  const [editDialog, setEditDialog] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const WASTE_GEN_UNITS = ["Tonnes", "Cubic Meter"];

  /** Shown next to Density input (tonnes per cubic metre). */
  const DENSITY_UNIT_LABEL = "Tonnes/m\u00b3";

  /** Show density (and require it on submit) for wastewater category or Effluent / Sewage waste types. */
  const showDensityField = (fd = formData) => requiresDensityAndVolumetricRow(fd);

  const syncManagementMethodsForWasteType = (wasteType) => {
    const map = wasteManagementMethodsByWasteType;
    const methods =
      map && wasteType && Array.isArray(map[wasteType]) ? map[wasteType] : [];
    setSelectedManagementMethods(methods);
  };

  // Component Rendering Logic
  const handleWasteTypeChange = (e) => {
    const selectedWasteType2 = e?.target?.value ?? element?.wasteType;
    syncManagementMethodsForWasteType(selectedWasteType2);
    setErrors((prev) => ({ ...prev, wasteType: "" }));
  };

  const [selectedWasteType, setSelectedWasteType] = useState(""); // State for selected waste type
  const [selectedManagementMethods, setSelectedManagementMethods] = useState(
    []
  ); // State for management methods
  /** Full vendor master list; options filtered by `wasteManagementType` vs selected method. */
  const [allWasteVendors, setAllWasteVendors] = useState([]);

  const fetchWasteVendors = async () => {
    try {
      const res = await get(`${api}/master/wasteManagement`);
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      setAllWasteVendors(rows);
    } catch (err) {
      console.error("Failed to load waste management vendors:", err);
    }
  };

  useEffect(() => {
    if (addData) {
      fetchWasteVendors();
    }
  }, [addData]);

  const vendorOptionsForMethod = (method, selectedVendorId) => {
    const m = String(method ?? "").trim();
    if (!m) return [];
    let matched = allWasteVendors.filter(
      (v) => String(v?.wasteManagementType ?? "").trim() === m
    );
    if (selectedVendorId) {
      const id = String(selectedVendorId);
      if (!matched.some((v) => String(v._id) === id)) {
        const current = allWasteVendors.find((v) => String(v._id) === id);
        if (current) matched = [...matched, current];
      }
    }
    return matched.map((item) => ({
      id: item._id,
      name: item.vendorLegalName,
      vendorLegalName: item.vendorLegalName,
    }));
  };

  const VENDOR_MASTER_ERROR =
    "No vendor available for this waste management method. Please add vendor in Masters.";

  const getVendorMasterError = (method) => {
    const m = String(method ?? "").trim();
    if (!m || allWasteVendors.length === 0) return "";
    const hasVendor = allWasteVendors.some(
      (v) => String(v?.wasteManagementType ?? "").trim() === m
    );
    return hasVendor ? "" : VENDOR_MASTER_ERROR;
  };

  useEffect(() => {
    const masterErr = getVendorMasterError(formData?.wasteManagementMethod);
    setErrors((prev) => {
      if (masterErr) {
        if (prev.vendorName === masterErr) return prev;
        return { ...prev, vendorName: masterErr };
      }
      if (prev.vendorName === VENDOR_MASTER_ERROR) {
        const next = { ...prev };
        delete next.vendorName;
        return next;
      }
      return prev;
    });
  }, [allWasteVendors, formData?.wasteManagementMethod]);

  useEffect(() => {
    console.log(initialState);
    setFormData(initialState);
    setFormData2(initialState);
    if (id && element) {
      setSelectedWasteType(element.wasteType);
      syncManagementMethodsForWasteType(element.wasteType);
    } else {
      setSelectedWasteType("");
      setSelectedManagementMethods([]);
    }
    setUploadedFiles(element?.documents || []);
  }, [element, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(name, value);
    if (name === "density") {
      // Allow only positive numeric input with optional decimal part.
      if (!/^\d*\.?\d*$/.test(value)) return;
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      setFormData((prevState) => ({ ...prevState, [name]: value }));
      return;
    }
    if (name.startsWith("quantityOfWaste")) {
      const parts = name.split(".");
      const field = parts[0];
      const subKey = parts[1];

      if (subKey === "unit") {
        setErrors((prev) => ({
          ...prev,
          [field]: { ...prev[field], unit: "" },
          ...(field === "quantityOfWasteGenerated" ? { density: "" } : {}),
        }));
        setFormData((prev) => {
          const next = {
            ...prev,
            [field]: { ...prev[field], unit: value },
          };
          if (field === "quantityOfWasteGenerated") {
            if (requiresDensityAndVolumetricRow(prev)) {
              next.quantityOfWasteManaged = {
                ...prev.quantityOfWasteManaged,
                unit: value,
              };
            }
          }
          return next;
        });
        return;
      }

      if (subKey !== undefined && subKey !== "value") return;
      if (!/^\d*\.?\d*$/.test(value)) return;

      setFormData((prevState) => ({
        ...prevState,
        [field]: {
          ...prevState[field],
          value: value,
        },
      }));

      setErrors((prevState) => ({
        ...prevState,
        [field]: {
          ...prevState[field],
          value: "",
        },
      }));
      return;
    } else if (name === "wasteCategory") {
      const normalizedCategory = String(value || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const isWastewaterCategory = normalizedCategory === "wastewater";
      const quantityUnit = isWastewaterCategory ? "" : "Tonnes";
      setErrors((prevErrors) => ({
        ...prevErrors,
        wasteCategory: "",
        wasteType: "",
        wasteManagementMethod: "",
      }));
      setSelectedWasteType("");
      setSelectedManagementMethods(isWastewaterCategory ? ["Recycling"] : []);
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
        wasteType: "",
        wasteManagementMethod: isWastewaterCategory ? "Recycling" : "",
        density: "",
        quantityOfWasteGenerated: {
          ...(prevState.quantityOfWasteGenerated || {}),
          unit: quantityUnit,
        },
        quantityOfWasteManaged: {
          ...(prevState.quantityOfWasteManaged || {}),
          unit: quantityUnit,
        },
        vendorName: "",
      }));
    } else if (name === "wasteType")
      setFormData((prevState) => {
        const isWastewaterCategory =
          String(prevState?.wasteCategory || "")
            .toLowerCase()
            .replace(/\s+/g, "") === "wastewater";
        const nextNeedsVol = requiresDensityAndVolumetricRow({
          ...prevState,
          wasteType: value,
        });
        const prevNeedsVol = requiresDensityAndVolumetricRow(prevState);

        let density = prevState.density;
        let quantityOfWasteGenerated = { ...prevState.quantityOfWasteGenerated };
        let quantityOfWasteManaged = { ...prevState.quantityOfWasteManaged };

        if (nextNeedsVol && !isWastewaterCategory && isDensityRequiredWasteType(value)) {
          if (!prevNeedsVol) {
            quantityOfWasteGenerated = { ...quantityOfWasteGenerated, unit: "" };
            quantityOfWasteManaged = { ...quantityOfWasteManaged, unit: "" };
          }
        } else if (!nextNeedsVol && prevNeedsVol) {
          density = "";
          quantityOfWasteGenerated = {
            ...quantityOfWasteGenerated,
            unit: "Tonnes",
          };
          quantityOfWasteManaged = {
            ...quantityOfWasteManaged,
            unit: "Tonnes",
          };
        }

        return {
          ...prevState,
          [name]: value,
          wasteManagementMethod: isWastewaterCategory ? "Recycling" : "",
          vendorName: "",
          density,
          quantityOfWasteGenerated,
          quantityOfWasteManaged,
        };
      });
    else if (name === "wasteManagementMethod") {
      const method = String(value ?? "").trim();
      const masterErr = getVendorMasterError(method);
      setErrors((prevErrors) => ({
        ...prevErrors,
        wasteManagementMethod: "",
        vendorName: masterErr || "",
      }));
      setFormData((prevState) => {
        const allowedIds = allWasteVendors
          .filter(
            (v) => String(v?.wasteManagementType ?? "").trim() === method
          )
          .map((v) => String(v._id));
        const keepVendor =
          prevState.vendorName &&
          allowedIds.includes(String(prevState.vendorName));
        return {
          ...prevState,
          [name]: value,
          vendorName: keepVendor ? prevState.vendorName : "",
        };
      });
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));

      setFormData((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (showDensityField()) {
      const densityRaw = String(formData?.density ?? "").trim();
      const densityValue = Number(densityRaw);
      if (!densityRaw) {
        newErrors.density = "Density is required.";
      } else if (!Number.isFinite(densityValue)) {
        newErrors.density = "Density must be a valid number.";
      } else if (densityValue < 0) {
        newErrors.density = "Density cannot be negative.";
      }
    }

    inputData.forEach((input) => {
      const value = formData[input.fieldName];
      console.log(input.fieldName, value);
      if (input.fieldName === "density") {
        if (!showDensityField()) return;
        return;
      }

      if (input.fieldName === "vendorName") {
        const masterErr = getVendorMasterError(formData.wasteManagementMethod);
        if (masterErr) {
          newErrors.vendorName = masterErr;
          return;
        }
      }

      if (
        input.fieldName === "quantityOfWasteManaged" &&
        formData.wasteManagementMethod === "Landfilling"
      )
        return;

      if (input.fieldName === "quantityOfWasteGenerated") {
        const cvValue = formData[input.fieldName]?.value || ""; // Ensure it's a string
        const cvUnit = formData[input.fieldName]?.unit || ""; // Ensure it's a string

        if (!validateNumber(cvValue)) {
          newErrors[input.fieldName] = {
            ...newErrors[input.fieldName],
            value: "Numeric Value is required.",
          };
        }

        if (requiresDensityAndVolumetricRow(formData) && !String(cvUnit).trim()) {
          newErrors[input.fieldName] = {
            ...newErrors[input.fieldName],
            unit: "Unit is required.",
          };
        }
        return; // Ensure to return after handling errors for calorificValue
      }
      const month = formData?.consumptionPeriod?.month;
      const year = formData?.consumptionPeriod?.year;

      if (input.fieldName === "consumptionPeriod") {
        if (!validateNumber(month)) newErrors["month"] = "Month is required";

        if (!validateNumber(year)) newErrors["year"] = "Year is required";

        return;
      }
      if (input.fieldName === "quantityOfWasteManaged") {
        const cvValue = formData[input.fieldName]?.value || ""; // Ensure it's a string
        if (!validateNumber(cvValue)) {
          newErrors[input.fieldName] = {
            ...newErrors[input.fieldName],
            value: "Numeric Value is required.",
          };
        } else if (
          Number(formData.quantityOfWasteGenerated?.value) < Number(cvValue)
        ) {
          newErrors[input.fieldName] = {
            ...newErrors[input.fieldName],
            value: "Waste Managed must be less than equal to Waste Generated",
          };
        }

        return; // Ensure to return after handling errors for calorificValue
      }

      if (input.type === "number") {
        if (!value) newErrors[input.fieldName] = `${input.title} is required.`;
        else if (!validateNumber(value))
          newErrors[input.fieldName] = numberError;
        return;
      }

      // Handle required fields
      if (
        ((input.type === "text" || input.type === "select") && input.fieldName!=="editRemarkReason") &&
        !value?.trim()
      ) {
        newErrors[input.fieldName] = `${input.title} is required.`;
      }
      if (element?._id!=="" && user?.endUser && input.fieldName === "editRemarkReason") {
        if (
          !formData[input.fieldName] ||
          formData[input.fieldName].trim() === ""
        ) {
          newErrors[input.fieldName] = "Edit Remark Reason is required";
        }
      }
    });
    console.log(newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const Save = async () => {
    if (!validateForm()) {
      return;
    }

    const url = id ? `${updateUrl}?Id=${id}` : addUrl;
    const dataToSend = { ...formData, documents: uploadedFiles };

    if (isWastewaterWasteCategory(formData.wasteCategory)) {
      dataToSend.wasteManagementMethod = "Recycling";
      dataToSend.quantityOfWasteManaged = {
        ...dataToSend.quantityOfWasteManaged,
        unit:
          dataToSend.quantityOfWasteGenerated?.unit ||
          dataToSend.quantityOfWasteManaged?.unit,
      };
    } else if (isDensityRequiredWasteType(formData.wasteType)) {
      dataToSend.quantityOfWasteManaged = {
        ...dataToSend.quantityOfWasteManaged,
        unit:
          dataToSend.quantityOfWasteGenerated?.unit ||
          dataToSend.quantityOfWasteManaged?.unit,
      };
    } else {
      dataToSend.quantityOfWasteGenerated = {
        ...dataToSend.quantityOfWasteGenerated,
        unit: "Tonnes",
      };
      dataToSend.quantityOfWasteManaged = {
        ...dataToSend.quantityOfWasteManaged,
        unit: "Tonnes",
      };
    }

    if (!showDensityField(formData)) {
      delete dataToSend.density;
    }

    try {
      if (user?.endUser && id) {
        dataToSend.requestStatus = "edit";
        dataToSend.requestMode = true;
        dataToSend.approvalStatus = "Pending";
      }
      const response = id
        ? await put(url, dataToSend)
        : await post(url, dataToSend);

      setsnackOpen({
        open: true,
        message: response.data.message,
        severity: "success",
      });

      await fetchData?.(dataToSend.wasteManagementMethod);
      setFormData(initialState);
      setUploadedFiles([]);
      handleClose();
    } catch (error) {
      console.error("Save Error:", error);

      if (error.response && error.response.data && error.response.data.errors) {
        const errorMessages = error.response.data.errors
          .map((err) => err.msg)
          .join(", ");
        setsnackOpen({
          open: true,
          message: errorMessages,
          severity: "error",
        });
      } else {
        alert(error.message || "An error occurred while saving the data.");
      }
    }
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    setFormData(initialState);
    setFormData2(initialState); // Reset form data to initial state
    setUploadedFiles(element?.documents || []);
    handleClose();
  };

  const handleExit = () => {
    const isFormDataUnchanged = Object.keys(formData).every(
      (key) => formData[key] === formData2[key]
    );
    const isFilesUnchanged =
      JSON.stringify(uploadedFiles) ===
      JSON.stringify(formData2?.documents || []);

    if (isFormDataUnchanged && isFilesUnchanged) {
      handleExitAgree();
    } else {
      setEditDialog(true); // Show the EditDialog if there are changes
    }
  };

  const renderInputField = (el) => {
    if (
      el.fieldName === "quantityOfWasteManaged" &&
      formData?.wasteManagementMethod === "Landfilling"
    )
      return;
    if (el.fieldName === "density") {
      if (!showDensityField()) return null;
    }
    if (el.fieldName === "quantityOfWasteGenerated") {
      const unitOptions = el.unitOptions?.length ? el.unitOptions : WASTE_GEN_UNITS;
      if (requiresDensityAndVolumetricRow(formData)) {
        const qtyGenRowHasError = !!(
          errors.quantityOfWasteGenerated?.value ||
          errors.quantityOfWasteGenerated?.unit
        );
        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", mt: 1, minHeight: "auto" }}
            >
              {formatTitleWithOptional(el.title)}
            </Typography>
            <Box
              display="flex"
              gap={1}
              sx={{
                width: "100%",
                alignItems: "flex-start",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <Box
                sx={{
                  flex: "1 1 58%",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <InputField
                  hideTitle
                  hideErrorTypography
                  ellipsis
                  title=""
                  handleChange={handleChange}
                  placeholder={el.placeholder}
                  value={formData.quantityOfWasteGenerated?.value ?? ""}
                  errors={errors.quantityOfWasteGenerated?.value}
                  fieldName={el.fieldName}
                  inputSx={WASTE_QTY_INPUT_ROOT_WASTEWATER}
                />
                <Box
                  sx={{
                    mt: qtyGenRowHasError ? 0.5 : 0,
                    minHeight: qtyGenRowHasError ? "40px" : 0,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  {errors.quantityOfWasteGenerated?.value ? (
                    <Typography variant="body2" sx={{ color: "#D34040", lineHeight: 1.3 }}>
                      {errors.quantityOfWasteGenerated.value}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Box
                sx={{
                  ...WASTE_GEN_UNIT_SELECT_COLUMN_SX,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <SelectField
                  hideTitle
                  hideErrorTypography
                  title=""
                  marginTop="0px"
                  mt={0}
                  allowClear
                  name="quantityOfWasteGenerated.unit"
                  value={formData.quantityOfWasteGenerated?.unit ?? ""}
                  handleChange={handleChange}
                  options={unitOptions}
                  errors={errors.quantityOfWasteGenerated?.unit}
                  defaultValue="Select unit"
                />
                <Box
                  sx={{
                    mt: qtyGenRowHasError ? 0.5 : 0,
                    minHeight: qtyGenRowHasError ? "40px" : 0,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  {errors.quantityOfWasteGenerated?.unit ? (
                    <Typography variant="body2" sx={{ color: "#D34040", lineHeight: 1.3 }}>
                      {errors.quantityOfWasteGenerated.unit}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      }
      return (
        <InputField
          title={formatTitleWithOptional(el.title)}
          handleChange={handleChange}
          placeholder={el.placeholder}
          value={formData.quantityOfWasteGenerated?.value ?? ""}
          errors={errors.quantityOfWasteGenerated?.value}
          fieldName={el.fieldName}
          adornmentText="Tonnes"
          inputSx={WASTE_QTY_VALUE_WITH_UNIT_SX}
        />
      );
    }
    if (el.fieldName === "consumptionPeriod") {
      return (
        <Box sx={{ mt: 1 }}>
          <MonthYearPicker
            {...{
              name: el.fieldName,
              setFormData,
              setFormData2,
              formData,
              errors,
              setErrors,
              title: el.title,
              // defaultMonth: formData?.consumptionPeriod?.month || "",
              // defaultYear: formData?.consumptionPeriod?.year || "",
            }}
          />
        </Box>
      );
    }
    // if (
    //   el.fieldName === "quantityOfWasteGenerated" ||
    //   el.fieldName === "quantityOfWasteManaged"
    // ) {
    //   return (
    //     <Box>
    //       <Typography
    //         variant="subtitle1"
    //         sx={{ fontWeight: "bold", mt: 1, mb: -1 }}
    //       >
    //         {el.title}
    //       </Typography>
    //       <Box display="flex" gap={1} sx={{ width: "100%" }}>
    //         <Box sx={{ width: "60%" }}>
    //           <InputField
    //             // title={el.title}
    //             handleChange={handleChange}
    //             placeholder={el.placeholder}
    //             value={formData[el.fieldName]?.value || ""}
    //             errors={errors[el.fieldName]?.value} // Access error for value
    //             fieldName={`${el.fieldName}.value`}
    //           />
    //         </Box>
    //         <Box sx={{ width: "40%" }}>
    //           <SelectField
    //             marginTop="0px"
    //             name={`${el.fieldName}.unit`}
    //             value={formData[el.fieldName]?.unit || ""}
    //             handleChange={handleChange}
    //             options={el.options}
    //             disable={el.fieldName === "quantityOfWasteManaged"}
    //             errors={errors[el.fieldName]?.unit} // Access error for unit
    //             // field={`${el.field}.unit`}
    //             defaultValue="Unit"
    //           />
    //         </Box>
    //       </Box>
    //     </Box>
    //   );
    // }
    if (el.fieldName === "editRemarkReason") {
      if (user?.endUser && id) {
        return (
          <Multiline
            title={formatTitleWithOptional(el.title)}
            handleChange={handleChange}
            placeholder={el.placeholder}
            value={formData[el.fieldName] || ""}
            errors={errors[el.fieldName]}
            fieldName={el.fieldName}
            required={true}
          />
        );
      }
      return null;
    }
    if (el.type === "select" || el.options) {
      const isWastewaterCategory =
        String(formData?.wasteCategory || "")
          .toLowerCase()
          .replace(/\s+/g, "") === "wastewater";
      const categoryKey = String(formData?.wasteCategory || "").trim();
      const typesForCategory =
        wasteTypesByCategory &&
        categoryKey &&
        Array.isArray(wasteTypesByCategory[categoryKey])
          ? wasteTypesByCategory[categoryKey]
          : null;
      let wasteTypeOptionsForCategory =
        el.fieldName === "wasteType"
          ? isWastewaterCategory
            ? ["Sludge", "Effluent"]
            : typesForCategory && typesForCategory.length > 0
              ? [...typesForCategory]
              : [...(el.options || [])]
          : el.options;
      if (
        el.fieldName === "wasteType" &&
        formData?.wasteType &&
        Array.isArray(wasteTypeOptionsForCategory) &&
        !wasteTypeOptionsForCategory.includes(formData.wasteType)
      ) {
        wasteTypeOptionsForCategory = [
          ...wasteTypeOptionsForCategory,
          formData.wasteType,
        ];
      }
      const wasteTypeSelectDisabled =
        el.fieldName === "wasteType" && !categoryKey;

      let wasteMethodSelectOptions = el.options;
      if (el.fieldName === "wasteManagementMethod") {
        if (isWastewaterCategory) {
          wasteMethodSelectOptions = ["Recycling"];
        } else {
          wasteMethodSelectOptions =
            selectedManagementMethods?.length > 0
              ? [...selectedManagementMethods]
              : [];
          if (
            formData?.wasteManagementMethod &&
            Array.isArray(wasteMethodSelectOptions) &&
            !wasteMethodSelectOptions.includes(formData.wasteManagementMethod)
          ) {
            wasteMethodSelectOptions = [
              ...wasteMethodSelectOptions,
              formData.wasteManagementMethod,
            ];
          }
        }
      }

      const wasteMgmtSelectDisabled =
        el.fieldName === "wasteManagementMethod" &&
        (isWastewaterCategory ||
          (!isWastewaterCategory && !String(formData?.wasteType || "").trim()));

      const vendorSelectOptions =
        el.fieldName === "vendorName"
          ? vendorOptionsForMethod(
              formData?.wasteManagementMethod,
              formData?.vendorName
            )
          : [];

      const vendorSelectDisabled =
        el.fieldName === "vendorName" &&
        !String(formData?.wasteManagementMethod || "").trim();

      return (
        <SelectField
          title={formatTitleWithOptional(el.title)}
          name={el.fieldName}
          value={formData[el.fieldName]}
          url={el.fieldName === "vendorName" ? undefined : el.url}
          field={el.field}
          height={isWastewaterCategory ? "40px" : undefined}
          handleChange={
            el.fieldName === "wasteType"
              ? (e) => {
                  setSelectedWasteType(e.target.value);
                  handleWasteTypeChange(e);
                  handleChange(e);
                }
              : handleChange
          }
          options={
            el.fieldName === "vendorName"
              ? vendorSelectOptions
              : el.fieldName === "wasteManagementMethod"
                ? wasteMethodSelectOptions
                : el.fieldName === "wasteType"
                  ? wasteTypeOptionsForCategory
                  : el.options
          }
          disable={
            wasteMgmtSelectDisabled ||
            wasteTypeSelectDisabled ||
            vendorSelectDisabled
          }
          errors={
            el.fieldName === "vendorName"
              ? errors[el.fieldName] ||
                getVendorMasterError(formData?.wasteManagementMethod)
              : errors[el.fieldName]
          }
        />
      );
    }

    if (el.type === "file") {
      return (
        <FileField
          title={formatTitleWithOptional(el.title)}
          name={el.fieldName}
          errors={errors[el.fieldName]}
          fileUrls={uploadedFiles}
          progress={uploadProgress[el.fieldName] || []}
          {...{ setUploadedFiles, setUploadProgress, setErrors }}
          // onDelete={handleDeleteFile}
        />
      );
    }

    const densityOrVolRow = showDensityField();
    const isDensityOrManaged =
      el.fieldName === "density" || el.fieldName === "quantityOfWasteManaged";

    return (
      <InputField
        title={
          el.fieldName === "density" && densityOrVolRow
            ? `${el.title} *`
            : formatTitleWithOptional(el.title)
        }
        handleChange={handleChange}
        placeholder={el.placeholder}
        value={
          el.nested ? formData[el.fieldName]?.value : formData[el.fieldName]
        }
        errors={
          el.nested ? errors[el.fieldName]?.value : errors[el.fieldName] || ""
        }
        fieldName={el.fieldName}
        ellipsis={densityOrVolRow && isDensityOrManaged ? true : undefined}
        adornmentText={
          el.fieldName === "density"
            ? DENSITY_UNIT_LABEL
            : el.fieldName === "quantityOfWasteManaged"
              ? densityOrVolRow
                ? formData?.quantityOfWasteGenerated?.unit ||
                  formData?.quantityOfWasteManaged?.unit ||
                  ""
                : "Tonnes"
              : ""
        }
        inputSx={
          isDensityOrManaged
            ? densityOrVolRow
              ? WASTE_QTY_VALUE_WITH_UNIT_SX_WASTEWATER
              : WASTE_QTY_VALUE_WITH_UNIT_SX
            : undefined
        }
      />
    );
  };

  const list = () => (
    <Box display="flex" gap={3} sx={{ width: "26rem", maxWidth: "100%" }}>
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "20px",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton onClick={handleExit} sx={{ color: "black" }}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box sx={{ width: "88%", p: 3, backgroundColor: "#fff" }}>
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
        >
          {id ? `Edit ${title} Data` : `Add ${title} Data`}
        </Typography>
        {inputData.map((el) => {
          // Conditional rendering for hours and industrial equipment

          return <div key={el.fieldName}>{renderInputField(el)}</div>;
        })}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleExit}
    >
      {list()}
    </Drawer>
  );
}

export default AddData;
